import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { breadcrumbs, SITE_URL } from "@/lib/seo";
import { Check, X, Minus, ArrowRight } from "@phosphor-icons/react";

const ROWS = [
  ["Entry price", "$9.99 / year (1 release)", "$22.99 / year (unlimited)", "$14.99 / year per single"],
  ["Unlimited releases", "$29.99 / year (Pro)", "Yes, on every plan", "$49.99 / year (Rising Artist)"],
  ["Royalties kept by the artist", "100%", "100%", "100%"],
  ["Released under a label name", "yes:Asylum Entertainment", "no:Your own name only", "no:Your own name only"],
  ["Human A&R review before release", "yes:Every release", "no:Automated", "no:Automated"],
  ["Roster accounts & royalty splits", "yes:Label tier ($79.99/yr)", "yes:Paid add-on", "yes:Higher tiers"],
  ["Pre-save pages & fan email capture", "yes:Included", "yes:HyperFollow", "yes:Paid add-on"],
  ["Release-day fan emails", "yes:Automatic", "no", "no"],
  ["Stores served", "150+", "150+", "150+"],
  ["Payout minimum", "$50 via PayPal", "No minimum", "$1"],
  ["Music stays up if you cancel", "no:Removed on cancel", "no:Removed unless Legacy add-on", "yes:Some plans"],
];

function Cell({ v }) {
  if (v.startsWith("yes:") || v === "yes") return <span className="inline-flex items-center gap-2 text-zinc-200"><Check size={14} weight="bold" className="text-green-500 shrink-0" />{v.slice(4)}</span>;
  if (v.startsWith("no:") || v === "no") return <span className="inline-flex items-center gap-2 text-zinc-500"><X size={14} weight="bold" className="text-red-500 shrink-0" />{v.slice(3)}</span>;
  if (v === "—") return <Minus size={14} className="text-zinc-600" />;
  return <span className="text-zinc-300">{v}</span>;
}

const FAQ = [
  ["Is Asylum Entertainment a DistroKid alternative?", "Yes. Asylum Entertainment is an independent music distribution label that, like DistroKid, charges a flat yearly fee and lets artists keep 100% of their streaming royalties. The difference is that Asylum releases your music under the Asylum Entertainment label name, with a human A&R review on every release, built-in pre-save pages and release-day fan emails."],
  ["Is Asylum Entertainment cheaper than TuneCore?", "For most independent artists, yes. Asylum's Starter plan is $9.99 per year for one release and the Pro plan is $29.99 per year for unlimited releases. TuneCore charges per release on its entry plan and $49.99 per year for unlimited singles and albums."],
  ["Do I keep my masters and royalties with Asylum?", "Yes. You keep full ownership of your masters and 100% of the royalties your music earns. Asylum never takes a commission — only the flat annual subscription fee."],
  ["Which is better for a label or collective: Asylum, DistroKid or TuneCore?", "Asylum's Label tier ($79.99/year) includes unlimited sub-artist accounts and per-release royalty splits with each artist seeing their own share on their own dashboard. DistroKid and TuneCore offer label features as separate paid add-ons or higher tiers."],
];

export default function Compare() {
  const jsonLd = [
    breadcrumbs([["Home", "/"], ["Asylum vs DistroKid vs TuneCore", "/compare"]]),
    { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    { "@type": "Article", headline: "Asylum Entertainment vs DistroKid vs TuneCore: independent music distribution compared", url: `${SITE_URL}/compare`, author: { "@id": `${SITE_URL}/#organization` }, publisher: { "@id": `${SITE_URL}/#organization` }, dateModified: "2026-06-01", description: "Side-by-side comparison of pricing, royalties, label features, pre-save tools and payouts." },
  ];
  return (
    <>
      <Seo title="Asylum vs DistroKid vs TuneCore — Music Distribution Compared" path="/compare"
        description="Compare Asylum Entertainment with DistroKid and TuneCore: yearly pricing from $9.99, 100% royalties, label-name releases, human A&R review, pre-save pages, roster splits and payouts." jsonLd={jsonLd} type="article" />
      <Navbar />
      <article>
        <section className="border-b border-zinc-800">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-4">— Compare</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95] mb-6 max-w-4xl" data-testid="compare-heading">
              Asylum vs DistroKid<br />vs <span className="text-red-600">TuneCore.</span>
            </h1>
            <p className="text-zinc-400 text-base md:text-lg max-w-3xl leading-relaxed">
              All three get your music onto Spotify, Apple Music, TIDAL and 150+ stores while you keep 100% of your royalties. Here is how an independent music distribution label compares with the two biggest self-serve distributors — honestly, including where they beat us.
            </p>
          </div>
        </section>

        <section className="border-b border-zinc-800">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 overflow-x-auto">
            <table className="w-full min-w-[800px] border border-zinc-800 text-sm" data-testid="compare-table">
              <thead>
                <tr className="bg-zinc-950 text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                  <th className="text-left p-4 border-b border-r border-zinc-800 font-normal">Feature</th>
                  <th className="text-left p-4 border-b border-r border-zinc-800 font-normal text-red-500">Asylum Entertainment</th>
                  <th className="text-left p-4 border-b border-r border-zinc-800 font-normal">DistroKid</th>
                  <th className="text-left p-4 border-b border-zinc-800 font-normal">TuneCore</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([f, a, d, t]) => (
                  <tr key={f} className="row-hover">
                    <th scope="row" className="text-left p-4 border-b border-r border-zinc-800 font-normal text-zinc-400">{f}</th>
                    <td className="p-4 border-b border-r border-zinc-800 bg-red-600/5"><Cell v={a} /></td>
                    <td className="p-4 border-b border-r border-zinc-800"><Cell v={d} /></td>
                    <td className="p-4 border-b border-zinc-800"><Cell v={t} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-zinc-600 mt-3">Competitor pricing and features as publicly listed in 2026; check their sites for current terms. DistroKid and TuneCore are trademarks of their respective owners.</p>
          </div>
        </section>

        <section className="border-b border-zinc-800">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <h2 className="font-display text-base md:text-lg uppercase tracking-[0.2em] text-red-500 mb-4">Who should pick Asylum</h2>
              <ul className="space-y-4 text-zinc-300 text-sm md:text-base leading-relaxed">
                <li>Artists who want their release to carry a <strong className="text-white">real label name</strong> on Spotify and Apple Music instead of "self-released".</li>
                <li>Artists who want a <strong className="text-white">human to check metadata, credits and artwork</strong> before it goes to stores — fewer rejections, fewer takedowns.</li>
                <li>Collectives and small imprints that need <strong className="text-white">roster accounts with automatic royalty splits</strong> included, not sold as an add-on.</li>
                <li>Anyone who wants pre-save pages, fan email capture and <strong className="text-white">release-day emails sent for them</strong>.</li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <h2 className="font-display text-base md:text-lg uppercase tracking-[0.2em] text-zinc-400 mb-4">Where DistroKid or TuneCore may fit better</h2>
              <ul className="space-y-4 text-zinc-400 text-sm md:text-base leading-relaxed">
                <li>You release dozens of tracks a month and want zero review time — DistroKid's instant automated pipeline is faster than any human A&R.</li>
                <li>You need a $1 or no-minimum payout. Asylum pays out via PayPal once your balance reaches $50.</li>
                <li>You want to publish strictly under your own artist/label name with no third-party label credit.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="border-b border-zinc-800">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
            <h2 className="font-display text-3xl uppercase tracking-tighter mb-10">Common questions</h2>
            <div className="grid md:grid-cols-2 gap-0 border border-zinc-800" data-testid="compare-faq">
              {FAQ.map(([q, a]) => (
                <div key={q} className="border-r border-b border-zinc-800 p-8">
                  <h3 className="font-display text-lg uppercase mb-3">{q}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/pricing" data-testid="compare-cta-pricing" className="bg-red-600 text-white px-8 py-4 text-sm tracking-[0.2em] uppercase inline-flex items-center gap-3 btn-brutal btn-brutal-red">See Asylum Plans <ArrowRight size={16} weight="bold" /></Link>
              <Link to="/how-it-works" data-testid="compare-cta-how" className="border border-zinc-700 px-8 py-4 text-sm tracking-[0.2em] uppercase inline-flex items-center gap-3 btn-brutal hover:border-white">How It Works</Link>
            </div>
          </div>
        </section>
      </article>
      <Footer />
    </>
  );
}
